const API_BASE = process.env.REACT_APP_API_BASE || '';

function getToken() {
  return localStorage.getItem('accessToken');
}

function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

function headers(json = true) {
  const h = {};
  if (json) h['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

let isRefreshing = false;
let refreshQueue = [];

function onRefreshed(newToken) {
  refreshQueue.forEach((cb) => cb(newToken));
  refreshQueue = [];
}

async function tryRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshQueue.push(resolve);
    });
  }

  isRefreshing = true;
  try {
    const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    const data = await res.json();
    if (data.success && data.data?.accessToken) {
      localStorage.setItem('accessToken', data.data.accessToken);
      if (data.data.refreshToken) {
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }
      // Update store if available
      try {
        const { default: useStore } = await import('./store/useStore');
        useStore.getState().setToken(data.data.accessToken, data.data.refreshToken);
      } catch { /* ignore */ }
      onRefreshed(data.data.accessToken);
      return data.data.accessToken;
    }
  } catch { /* ignore */ }
  finally {
    isRefreshing = false;
  }
  return null;
}

function needsRefresh(res) {
  // Spring Security often returns 403 (not 401) for expired/invalid JWTs,
  // so treat both as a signal to try refreshing the access token.
  return (res.status === 401 || res.status === 403) && !!getToken();
}

async function handleUnauthorized(res, retryFn) {
  if (!needsRefresh(res)) return res;

  const newToken = await tryRefresh();
  if (newToken) {
    return retryFn(newToken);
  }

  // Refresh failed — logout
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/';
  return res;
}

export async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (needsRefresh(res)) {
    return handleUnauthorized(res, (token) =>
      fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      })
    );
  }
  return res;
}

export async function apiPostRaw(path, rawBody) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: headers(),
    body: rawBody,
  });
  if (needsRefresh(res)) {
    return handleUnauthorized(res, (token) =>
      fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: rawBody,
      })
    );
  }
  return res;
}

export async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: headers(false),
  });
  if (needsRefresh(res)) {
    return handleUnauthorized(res, (token) =>
      fetch(`${API_BASE}${path}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
    );
  }
  return res;
}

export async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: headers(false),
  });
  if (needsRefresh(res)) {
    return handleUnauthorized(res, (token) =>
      fetch(`${API_BASE}${path}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
    );
  }
  return res;
}

export async function apiGetBlob(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: headers(false),
  });
  if (needsRefresh(res)) {
    const retryRes = await handleUnauthorized(res, (token) =>
      fetch(`${API_BASE}${path}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
    );
    return retryRes.blob();
  }
  return res.blob();
}

export { API_BASE };
